import type { FetchWithAuth } from '../auth';
import env from '../configs/env';

export interface FileDownloadParams {
  cedantName: string;
  id: string;
  actualFileName: string;
  version?: string;
}

export interface FolderDownloadParams {
  cedantName: string;
  directoryId: string;
  zipFileName?: string;
}

export class FileDownloadService {
  static async downloadFile(
    params: FileDownloadParams,
    token: string,
    fetchWithAuth: FetchWithAuth,
  ): Promise<Blob> {
    const { cedantName, id, actualFileName, version } = params;
    
    const url = new URL(`${env.api.host}${env.api.paths.fileDownload}`);
    url.searchParams.set('cedantName', cedantName);
    url.searchParams.set('id', id);
    url.searchParams.set('actualFileName', actualFileName);
    if (version) {
      url.searchParams.set('version', version);
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await fetchWithAuth(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  static async downloadFolder(
    params: FolderDownloadParams,
    token: string,
    fetchWithAuth: FetchWithAuth,
  ): Promise<void> {
    const { cedantName, directoryId, zipFileName } = params;
    
    const url = new URL(`${env.api.host}${env.api.paths.folderDownload}`);
    url.searchParams.set('cedantName', cedantName);
    url.searchParams.set('directoryId', directoryId);
    if (zipFileName) {
      url.searchParams.set('zipFileName', zipFileName);
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await fetchWithAuth(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Folder download failed: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || zipFileName || 'folder.zip';
    
    this.downloadBlob(blob, filename);
  }

  static downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}